import { useState } from "react";
import {
  ChecklistStep,
  MarkAsReadButton,
  OpenPdfButton,
  ReadBadge,
  ReviewSignOffButton,
  SignedOffBadge,
} from "@/components/FTUXShared";
import { FTUX_PDF_URLS } from "@/constants/gettingStarted";
import { useGettingStarted } from "@/hooks/useGettingStarted";
import { useToast } from "@/hooks/useToast";
import { CodeOfConductModal } from "@/pages/dashboard/components/advisor-home/components/CodeOfConductModal";
import { trackGettingStarted } from "../gettingStartedTracking";

/**
 * The body of Getting Started Phase 1 · step 2 "Code of Conduct": the expert
 * onboarding checklist that used to sit on the XO Home page, moved here row
 * for row — same FTUXShared rows and buttons, same behaviour, same records:
 *
 *   1. Code of Conduct      Review & Sign Off → Signed Off   (ftux step 1)
 *   2. Expert explainer     Open PDF → Mark as Read → Read   (ftux step 2)
 *   3. Achiever archetypes  Open PDF → Mark as Read → Read   (ftux step 3)
 *
 * "Mark as Read" appears only after the PDF has been opened in this visit, as
 * on Home. The Getting Started step ticks itself once all three are done.
 * Analytics: getting_started_code_of_conduct_review_clicked / _signed,
 * getting_started_explainer_pdf_opened / _marked_read, and the same for
 * archetypes (see gettingStartedTracking.ts).
 */
export const OnboardingChecklist = () => {
  const { showToast } = useToast();
  const { onboarding, recordOnboarding, isRecordingOnboarding } = useGettingStarted();
  const [conductOpen, setConductOpen] = useState(false);
  const [pdfOpened, setPdfOpened] = useState({ explainer: false, archetypes: false });

  const record = (ftuxStep: 1 | 2 | 3, eventName: string) =>
    recordOnboarding(ftuxStep)
      .then(() => {
        trackGettingStarted(eventName);
        return true;
      })
      .catch(() => {
        showToast({ type: "error", message: "Couldn't save that — please try again." });
        return false;
      });

  const openPdf = (which: "explainer" | "archetypes") => {
    const url =
      which === "explainer" ? FTUX_PDF_URLS.advisorExplainer : FTUX_PDF_URLS.achieverArchetypes;
    window.open(url, "_blank", "noopener,noreferrer");
    trackGettingStarted(`${which}_pdf_opened`);
    setPdfOpened((prev) => ({ ...prev, [which]: true }));
  };

  return (
    <div className="mt-4 overflow-hidden rounded-[14px] border border-theme-border bg-theme-bg-surface">
      <ChecklistStep
        stepNumber={1}
        title="Code of Conduct"
        description="Review our community standards and sign off to confirm your agreement."
        completed={onboarding.conductSigned}
        required={!onboarding.conductSigned}
        tooltipText="You must review and sign the Code of Conduct before you can receive Achiever matches or Personal Advisory Board invitations. This is a mandatory first step in your advisory journey."
        actions={
          onboarding.conductSigned ? (
            <SignedOffBadge />
          ) : (
            <ReviewSignOffButton
              onClick={() => {
                trackGettingStarted("code_of_conduct_review_clicked");
                setConductOpen(true);
              }}
            />
          )
        }
      />

      <ChecklistStep
        stepNumber={2}
        title="Expert / Coach / Mentor Explainer"
        description="Understand your role and how to show up effectively for Achievers."
        completed={onboarding.explainerRead}
        actions={
          <>
            <OpenPdfButton onClick={() => openPdf("explainer")} />
            {onboarding.explainerRead ? (
              <ReadBadge />
            ) : (
              pdfOpened.explainer && (
                <MarkAsReadButton onClick={() => void record(2, "explainer_marked_read")} />
              )
            )}
          </>
        }
      />

      <ChecklistStep
        stepNumber={3}
        title="Common Achiever Archetypes"
        description="Learn about the people you will advise, their goals, challenges, and mindsets."
        descriptionLine2="Stored permanently in Resources."
        completed={onboarding.archetypesRead}
        isLast
        actions={
          <>
            <OpenPdfButton onClick={() => openPdf("archetypes")} />
            {onboarding.archetypesRead ? (
              <ReadBadge />
            ) : (
              pdfOpened.archetypes && (
                <MarkAsReadButton onClick={() => void record(3, "archetypes_marked_read")} />
              )
            )}
          </>
        }
      />

      <CodeOfConductModal
        open={conductOpen}
        onOpenChange={setConductOpen}
        isLoading={isRecordingOnboarding}
        onConfirm={() => {
          void record(1, "code_of_conduct_signed").then((saved) => {
            if (saved) setConductOpen(false);
          });
        }}
      />
    </div>
  );
};

export default OnboardingChecklist;
